import React, { useMemo, useState } from 'react';
import {
  Input,
  Dropdown,
  Option,
  Label,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ErrorCircle20Filled } from '@fluentui/react-icons';
import { US_STATES, CA_PROVINCES, REGION_NAMES } from '../../../shared/regions';

/**
 * Shared address-fields surface used by both the customer-profile address
 * section and the order's billing-address dialog. Owns the country-driven
 * region/postal labels, the postal-code masking + validation logic, and the
 * region option list. Layout is a vertical stack with a 2/2/1 row for
 * City / Region / Postal at the bottom.
 *
 * What this component does NOT own:
 *   - The "Saved address" picker that the billing dialog adds on top.
 *   - The billing-name override block (Individuals only) that the customer
 *     form adds above the address fields.
 * Both of those stay in their respective wrappers.
 */
interface AddressFieldsProps {
  addressLine1: string;
  onAddressLine1Change: (value: string) => void;
  addressLine2: string;
  onAddressLine2Change: (value: string) => void;
  city: string;
  onCityChange: (value: string) => void;
  region: string;
  onRegionChange: (value: string) => void;
  postalCode: string;
  onPostalCodeChange: (value: string) => void;
  country: string;
  onCountryChange: (value: string) => void;
  /**
   * Fires after a field commits (input blur with valid postal, dropdown
   * selection, or country/region change). Used by the customer form to
   * trigger autosave; the billing dialog can ignore it.
   */
  onCommit?: () => void;
  /**
   * Notifies the caller when the postal validation state flips so
   * downstream forms can mirror the error into their own state (e.g.
   * useCustomerForm.fieldErrors). Receives null when the field becomes
   * valid or empty.
   */
  onPostalErrorChange?: (error: string | null) => void;
  /** Disable all fields (e.g. while editing is locked). */
  disabled?: boolean;
  /** Treat fields as read-only — same visual lock as `disabled`. */
  readOnly?: boolean;
  /** Override the auto-generated input IDs to avoid duplicate-id collisions
   *  when two AddressFields render on the same page. Defaults to "address". */
  idPrefix?: string;
  /** When true, the form is treated as having required fields: Address
   *  line 2 gets an "(optional)" suffix on its label, and the other fields
   *  read as required by convention (no asterisks — they'd be noise on a
   *  5-of-6-required form). Validation/enforcement is the caller's
   *  responsibility (e.g. disabling Save when fields are blank). */
  required?: boolean;
}

const useStyles = makeStyles({
  fields: {
    display: 'flex',
    flexDirection: 'column',
    // Matches `formField`'s 12px paddingBottom so rows in the customer
    // profile's General and Billing-address columns align vertically.
    ...shorthands.gap('12px'),
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
  },
  row: {
    display: 'grid',
    // City shrinks so the Region dropdown gets enough width for long state
    // names (e.g. "New Hampshire") to stay on one line instead of wrapping.
    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1.3fr) minmax(0, 1fr)',
    ...shorthands.gap('12px'),
    // Below 768px the city/region/postal row is too cramped — stack the
    // three fields so each gets full width.
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  // Region dropdown trigger: long state/province names ("Newfoundland and
  // Labrador") overflow the narrow column. Truncate the selected value with
  // an ellipsis on one line instead of letting the button wrap.
  regionDropdown: {
    '& button': {
      whiteSpace: 'nowrap',
      ...shorthands.overflow('hidden'),
      textOverflow: 'ellipsis',
    },
  },
  // Muted "(optional)" suffix used on Address line 2 when the form is in
  // required-mode. Lower-emphasis weight/color so it reads as supporting
  // text, not part of the field label.
  optionalSuffix: {
    color: tokens.colorNeutralForeground3,
    fontWeight: 400,
    marginLeft: '6px',
    fontSize: '13px',
  },
  // Tooltip content for postal validation. Renders a red callout with an
  // icon so it reads unambiguously as an error rather than a generic hint.
  postalErrorTooltip: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    color: tokens.colorPaletteRedForeground1,
    fontSize: '13px',
    fontWeight: 500,
  },
});

const validatePostalCode = (value: string, country: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (country === 'United States') {
    return /^\d{5}$/.test(trimmed) ? null : 'ZIP must be 5 digits.';
  }
  if (country === 'Canada') {
    return /^[A-Z]\d[A-Z] \d[A-Z]\d$/.test(trimmed) ? null : 'Postal code must be in A1A 1A1 format.';
  }
  return null;
};

// Strip/normalize as the user types so the input self-formats. US is digits
// only (max 5). Canada follows A1A 1A1 — letter, digit, letter, space, digit,
// letter, digit — with an auto-inserted space after the 3rd character.
const maskPostalCodeOnInput = (input: string, country: string): string => {
  if (country === 'United States') {
    return input.replace(/\D/g, '').slice(0, 5);
  }
  if (country === 'Canada') {
    const compact = input.toUpperCase().replace(/\s/g, '');
    let result = '';
    for (let i = 0; i < Math.min(compact.length, 6); i++) {
      const ch = compact[i];
      if (i % 2 === 0 ? /[A-Z]/.test(ch) : /\d/.test(ch)) {
        result += ch;
      }
    }
    return result.length > 3 ? `${result.slice(0, 3)} ${result.slice(3)}` : result;
  }
  return input;
};

// Final normalize on blur — handles paste-without-space cases for Canada.
const normalizePostalCodeOnBlur = (value: string, country: string): string => {
  if (country === 'Canada') {
    const compact = value.replace(/\s+/g, '');
    if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(compact)) {
      return `${compact.slice(0, 3)} ${compact.slice(3)}`;
    }
  }
  return value;
};

const AddressFields: React.FC<AddressFieldsProps> = ({
  addressLine1,
  onAddressLine1Change,
  addressLine2,
  onAddressLine2Change,
  city,
  onCityChange,
  region,
  onRegionChange,
  postalCode,
  onPostalCodeChange,
  country,
  onCountryChange,
  onCommit,
  onPostalErrorChange,
  disabled = false,
  readOnly = false,
  idPrefix = 'address',
  required = false,
}) => {
  const styles = useStyles();
  const [postalError, setPostalError] = useState<string | null>(null);

  const regionOptions = useMemo(() => {
    if (country === 'Canada') return CA_PROVINCES;
    if (country === 'United States') return US_STATES;
    return [];
  }, [country]);

  const regionLabel =
    country === 'United States' ? 'State' :
    country === 'Canada' ? 'Province' : 'Region';
  const postalLabel = country === 'Canada' ? 'Postal code' : 'Zip code';

  // Required-mode adds Fluent's built-in red asterisk to each required
  // label. Skipped in read-only contexts since the field can't be edited.
  const showRequired = required && !readOnly;

  const setPostalErr = (err: string | null) => {
    setPostalError(err);
    onPostalErrorChange?.(err);
  };

  return (
    <div className={styles.fields}>
      <div className={styles.field}>
        <Label htmlFor={`${idPrefix}-country`} weight="semibold">Country</Label>
        {readOnly ? (
          <Input
            id={`${idPrefix}-country`}
            size="large"
            value={country}
            readOnly
            style={{ width: '100%', minWidth: 0 }}
          />
        ) : (
          <Dropdown
            id={`${idPrefix}-country`}
            size="large"
            value={country}
            selectedOptions={[country]}
            onOptionSelect={(_, data) => {
              const next = (data.optionValue as string) || '';
              onCountryChange(next);
              onRegionChange('');
              onCommit?.();
            }}
            disabled={disabled}
            style={{ width: '100%', minWidth: 0 }}
          >
            <Option value="Canada">Canada</Option>
            <Option value="United States">United States</Option>
          </Dropdown>
        )}
      </div>

      <div className={styles.field}>
        <Label htmlFor={`${idPrefix}-line1`} weight="semibold">Address line 1</Label>
        <Input
          id={`${idPrefix}-line1`}
          size="large"
          value={addressLine1}
          placeholder={readOnly ? undefined : "Address"}
          onChange={(_, data) => onAddressLine1Change(data.value)}
          onBlur={onCommit}
          disabled={disabled}
          readOnly={readOnly}
          style={{ width: '100%', minWidth: 0 }}
        />
      </div>

      <div className={styles.field}>
        <Label htmlFor={`${idPrefix}-line2`} weight="semibold">
          Address line 2
          {showRequired && <span className={styles.optionalSuffix}>(optional)</span>}
        </Label>
        <Input
          id={`${idPrefix}-line2`}
          size="large"
          value={addressLine2}
          placeholder={readOnly ? undefined : "Apartment, suite, etc. (optional)"}
          onChange={(_, data) => onAddressLine2Change(data.value)}
          onBlur={onCommit}
          disabled={disabled}
          readOnly={readOnly}
          style={{ width: '100%', minWidth: 0 }}
        />
      </div>

      <div className={styles.row}>
        <div className={styles.field} style={{ minWidth: 0 }}>
          <Label htmlFor={`${idPrefix}-city`} weight="semibold">City</Label>
          <Input
            id={`${idPrefix}-city`}
            size="large"
            value={city}
            placeholder={readOnly ? undefined : "City"}
            onChange={(_, data) => onCityChange(data.value)}
            onBlur={onCommit}
            disabled={disabled}
            readOnly={readOnly}
            style={{ width: '100%', minWidth: 0 }}
          />
        </div>

        <div className={styles.field} style={{ minWidth: 0 }}>
          <Label htmlFor={`${idPrefix}-region`} weight="semibold">{regionLabel}</Label>
          {readOnly ? (
            <Input
              id={`${idPrefix}-region`}
              size="large"
              value={region ? (REGION_NAMES[region] || region) : ''}
              readOnly
              style={{ width: '100%', minWidth: 0 }}
            />
          ) : (
            <Dropdown
              id={`${idPrefix}-region`}
              size="large"
              className={styles.regionDropdown}
              placeholder="Select..."
              value={region ? (REGION_NAMES[region] || region) : ''}
              selectedOptions={region ? [region] : []}
              onOptionSelect={(_, data) => {
                const code = (data.optionValue as string) || '';
                onRegionChange(code);
                onCommit?.();
              }}
              disabled={disabled}
              style={{ width: '100%', minWidth: 0 }}
            >
              {regionOptions.map((r) => (
                <Option key={r.code} value={r.code}>{r.name}</Option>
              ))}
            </Dropdown>
          )}
        </div>

        <div className={styles.field} style={{ minWidth: 0 }}>
          <Label htmlFor={`${idPrefix}-postal`} weight="semibold">{postalLabel}</Label>
          {/* Tooltip overlays the validation message so flipping in/out of
              the error state doesn't push neighboring fields around. The red
              outline on the Input is the persistent visual cue; the tooltip
              spells out the format requirement. */}
          <Tooltip
            content={
              <span className={styles.postalErrorTooltip}>
                <ErrorCircle20Filled />
                {postalError || ''}
              </span>
            }
            visible={!!postalError}
            relationship="description"
            withArrow
            positioning="above"
          >
            <Input
              id={`${idPrefix}-postal`}
              size="large"
              value={postalCode}
              inputMode={country === 'United States' ? 'numeric' : 'text'}
              maxLength={country === 'United States' ? 5 : 7}
              aria-invalid={!!postalError}
              placeholder={
                readOnly ? undefined :
                country === 'United States' ? '12345' :
                country === 'Canada' ? 'H3Z 2Y7' : ''
              }
              onChange={(_, data) => {
                onPostalCodeChange(maskPostalCodeOnInput(data.value, country));
                if (postalError) setPostalErr(null);
              }}
              onBlur={() => {
                const normalized = normalizePostalCodeOnBlur(postalCode, country);
                if (normalized !== postalCode) onPostalCodeChange(normalized);
                const err = validatePostalCode(normalized, country);
                setPostalErr(err);
                if (!err) onCommit?.();
              }}
              disabled={disabled}
              readOnly={readOnly}
              style={{
                width: '100%',
                minWidth: 0,
                ...(postalError ? { outline: `1px solid ${tokens.colorPaletteRedBorder1}` } : {}),
              }}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default AddressFields;
